/**
 * Proposal Form Component
 * Modal form for creating and editing work proposals
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { proposalsApi } from '../../services/wmsService';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/context/AuthContext';
import { udaMapView } from '../../lib/udaGeo';
// `type` marker required: GeoData is an interface, and esbuild (Vite dev) strips types
// per-file, so a value import emits a runtime import for a non-existent export.
import GeoLocation, { type GeoData } from '../GeoLocation';
import { useWorkMasterList } from '../../hooks/useWorkMasterList';
import type { WorkType, WorkCategory, Scheme, FinancialYear, ProposalSource, ProposalType, PriorityLevel, Department } from '../../lib/masterTypes';
import { todayYmd } from '../../lib/dateUtils';
import { PROCUREMENT_TYPE_OPTIONS, normaliseProcurementType, type ProcurementType } from '../../lib/procurementType';
import { UdaRequiredField } from '../common/UdaRequiredField';
import { ListSkeleton } from '@/components/base/Skeleton';
import DraftRestoredBanner from '@/components/base/DraftRestoredBanner';
import { useFormDraft } from '@/hooks/useFormDraft';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface ProposalFormData {
  // The UDA the proposal belongs to. Mandatory; works created from it inherit it.
  uda_id: number | null;
  prpsl_cd: string;
  prpsl_nm: string;
  prpsl_dscn_tx: string;
  prpsl_srce_id: number | null;
  prpsl_type_cd: string;
  // WORKS / GOODS / SERVICES (server migration 217)
  prcrmnt_type_cd: ProcurementType;
  prpsl_sts_cd: string;
  wrk_type_id: number | null;
  wrk_ctgry_id: number | null;
  schme_id: number | null;
  sub_schme_id: number | null;
  fnncl_yr_id: number | null;
  fndg_agncy_id: number | null;
  dpmt_id: number | null;
  fnctn_id: number | null;
  bdgt_hd_id: number | null;
  zone_id: number | null;
  ward_id: number | null;
  lctn_tx: string;
  estmt_cst_at: number;
  prpsd_by_nm: string;
  prpsd_by_dsgntn: string;
  prpsd_by_cntct: string;
  prpsl_dt: string;
  aprv_for_estmt_dt: string;
  estmt_prprd_dt: string;
  prty_cd: string;
  jstfctn_tx: string;
  rmrks_tx: string;
  bnfcry_cnt: number;
  bnfcry_dscn_tx: string;
  geo_data: GeoData | null;
}

interface ProposalWorkDraft {
  wrk_nm: string;
  lctn_tx: string;
  allctd_at: number;
}

interface ProposalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  proposalId?: number | null;
}

const initialFormData: ProposalFormData = {
  uda_id: null,
  prpsl_cd: '',
  prpsl_nm: '',
  prpsl_dscn_tx: '',
  prpsl_srce_id: null,
  prpsl_type_cd: 'NEW',
  prcrmnt_type_cd: 'WORKS',
  prpsl_sts_cd: 'DRAFT',
  wrk_type_id: null,
  wrk_ctgry_id: null,
  schme_id: null,
  sub_schme_id: null,
  fnncl_yr_id: null,
  fndg_agncy_id: null,
  dpmt_id: null,
  fnctn_id: null,
  bdgt_hd_id: null,
  zone_id: null,
  ward_id: null,
  lctn_tx: '',
  estmt_cst_at: 0,
  prpsd_by_nm: '',
  prpsd_by_dsgntn: '',
  prpsd_by_cntct: '',
  prpsl_dt: todayYmd(),
  aprv_for_estmt_dt: '',
  estmt_prprd_dt: '',
  prty_cd: 'MEDIUM',
  jstfctn_tx: '',
  rmrks_tx: '',
  bnfcry_cnt: 0,
  bnfcry_dscn_tx: '',
  geo_data: null
};

// Section Header Component
function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg">
      <i className={`${icon} text-white`}></i>
      <h4 className="font-medium text-white">{title}</h4>
    </div>
  );
}

interface ValidationErrors {
  uda_id?: string;
  prpsl_nm?: string;
  prpsl_type_cd?: string;
  prpsl_dt?: string;
  fnncl_yr_id?: string;
  wrk_ctgry_id?: string;
  wrk_type_id?: string;
  schme_id?: string;
  estmt_cst_at?: string;
  prpsd_by_nm?: string;
  prpsd_by_dsgntn?: string;
  // Budgetary Details. Sub Scheme is deliberately absent — it is the one field in
  // that section not marked required on screen.
  fndg_agncy_id?: string;
  dpmt_id?: string;
  fnctn_id?: string;
  bdgt_hd_id?: string;
}

type RequiredFieldKey = keyof ValidationErrors;

interface MissingRequiredField {
  field: RequiredFieldKey;
  label: string;
  message: string;
}

/** Anchor id on the input for a required field, so the alert banner can scroll to it. */
const requiredFieldAnchor = (field: RequiredFieldKey) => `prpsl-req-${field}`;

/**
 * The single source of truth for "is this proposal complete enough to save".
 *
 * Pulled out of validateForm() so the same answer drives three things that must never
 * disagree: the red alert banner, the disabled state of the save button, and the
 * per-field errors set when a save is attempted. Previously only the third existed, so
 * the operator learned what was missing only after clicking — on a seven-section form
 * where Estimated Cost sits far below Proposal Title, that meant a toast naming a field
 * that was nowhere on screen.
 *
 * Pure: it sets no state and shows no toast, so it is safe to call from a useMemo on
 * every render.
 *
 * `masterCounts` carries how many options each finance dropdown actually has. An empty
 * list is NOT treated as a missing value — those three lists come from the finance
 * module behind its own permission, and masterListStore uses allSettled, so a profile
 * without finance read access gets a 403 that arrives as an empty array. Demanding a
 * choice the operator cannot make would make the form permanently unsubmittable, so an
 * empty list is reported separately (unavailableMasters) and lets the save through.
 */
function collectMissingRequired(
  formData: ProposalFormData,
  masterCounts: Record<'fndg_agncy_id' | 'dpmt_id' | 'fnctn_id' | 'bdgt_hd_id', number>
): { missing: MissingRequiredField[]; unavailableMasters: string[] } {
  const missing: MissingRequiredField[] = [];
  const unavailableMasters: string[] = [];
  const add = (field: RequiredFieldKey, label: string, message: string) =>
    missing.push({ field, label, message });

  // Checked in the order the fields appear on screen, so the alert banner reads top to
  // bottom rather than in an arbitrary order the operator has to hunt through.
  if (!formData.uda_id) add('uda_id', 'UDA', 'Please select a UDA');
  if (!formData.prpsl_type_cd) add('prpsl_type_cd', 'Proposal Type', 'Please select a proposal type');
  if (!formData.prpsl_dt) add('prpsl_dt', 'Proposal Date', 'Proposal date is required');
  if (!formData.fnncl_yr_id) add('fnncl_yr_id', 'Financial Year', 'Please select a financial year');

  if (!formData.prpsl_nm.trim()) {
    add('prpsl_nm', 'Proposal Title', 'Proposal title is required');
  } else if (formData.prpsl_nm.trim().length < 5) {
    add('prpsl_nm', 'Proposal Title (min 5 chars)', 'Proposal title must be at least 5 characters');
  }

  // Work category and type are construction sectors (Roads, Water Supply, CC Road...).
  // A goods or services purchase has none that fit, so they are required only for
  // WORKS. The server applies the same rule at submission.
  if (formData.prcrmnt_type_cd === 'WORKS') {
    if (!formData.wrk_ctgry_id) add('wrk_ctgry_id', 'Work Category', 'Please select a work category');
    if (!formData.wrk_type_id) add('wrk_type_id', 'Work Type/Sector', 'Please select a work type');
  }

  const budgetFields: Array<{ field: keyof typeof masterCounts; label: string; value: number | null }> = [
    { field: 'fndg_agncy_id', label: 'Funding Agency', value: formData.fndg_agncy_id },
    { field: 'dpmt_id', label: 'Department', value: formData.dpmt_id },
    { field: 'fnctn_id', label: 'Function / Cost Center', value: formData.fnctn_id },
    { field: 'bdgt_hd_id', label: 'Budget Head', value: formData.bdgt_hd_id },
  ];
  for (const { field, label, value } of budgetFields) {
    if (value) continue;
    if (!masterCounts[field]) {
      unavailableMasters.push(label);
      continue;
    }
    add(field, label, `Please select a ${label.toLowerCase()}`);
  }

  if (!formData.schme_id) add('schme_id', 'Scheme', 'Please select a scheme');

  if (!formData.estmt_cst_at || formData.estmt_cst_at <= 0) {
    add('estmt_cst_at', 'Estimated Cost', 'Estimated cost must be greater than 0');
  }

  if (!formData.prpsd_by_nm.trim()) add('prpsd_by_nm', 'Proposed By', 'Proposer name is required');
  if (!formData.prpsd_by_dsgntn.trim()) {
    add('prpsd_by_dsgntn', 'Proposer Designation', 'Proposer designation is required');
  }

  return { missing, unavailableMasters };
}

export default function ProposalForm({ isOpen, onClose, onSuccess, proposalId }: ProposalFormProps) {
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [formData, setFormData] = useState<ProposalFormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [worksCount, setWorksCount] = useState(0);
  // Works already created from this proposal (shown once it is converted).
  const [linkedWorks, setLinkedWorks] = useState<Array<{ wrk_id: number; wrk_cd: string; wrk_nm: string; allctd_at: number | null; wrk_sts_nm?: string }>>([]);
  const [workDrafts, setWorkDrafts] = useState<ProposalWorkDraft[]>([
    { wrk_nm: '', lctn_tx: '', allctd_at: 0 },
  ]);

  // Opening view for the Geo Location map. Switching UDA in the header reloads the
  // page (Header.tsx handleUdaChange), so user.udaId is always the active UDA here.
  const { user } = useAuth();
  const mapView = useMemo(() => udaMapView((user as any)?.udaId), [user]);

  // Masters data (from centralized store) — fundingAgencies/functionCenters/
  // budgetHeads/subSchemes come from the legacy ERP finance module via
  // wmsService mappers (GET /apiv1/finance/{fund,function,budget-group,subscheme}).
  const masterStore = useWorkMasterList();
  const {
    workTypes, workCategories, schemes, financialYears,
    proposalSources, proposalTypes, priorityLevels, departments,
  } = masterStore;
  // These four finance masters are not (yet) provided by the master-list store.
  // Default them to empty arrays so the modal renders instead of crashing the
  // whole app on `.map` of undefined (white screen on "New Proposal").
  const fundingAgencies = (masterStore as any).fundingAgencies ?? [];
  const functionCenters = (masterStore as any).functionCenters ?? [];
  const budgetHeads = (masterStore as any).budgetHeads ?? [];
  const subSchemes = (masterStore as any).subSchemes ?? [];

  const isEdit = !!proposalId;

  // Draft of a NEW proposal, kept in localStorage so closing the popup part-way does
  // not lose the entry. Edits are not drafted: the saved record is the source of truth.
  // Only fields the user changed are kept. The blank form already carries defaults
  // (type NEW, WORKS, MEDIUM, today's date, the current FY), and storing those would
  // offer a "restored" draft on every open even when nothing was typed.
  const currentFyId = financialYears.find((fy) => fy.crnt_in)?.fy_id ?? null;
  const draftValues = useMemo(() => {
    const changed: Record<string, any> = {};
    (Object.keys(formData) as Array<keyof ProposalFormData>).forEach((key) => {
      if (key === 'prpsl_sts_cd' || key === 'prpsl_cd') return;
      if (key === 'fnncl_yr_id' && formData.fnncl_yr_id === currentFyId) return;
      if (formData[key] !== initialFormData[key]) changed[key] = formData[key];
    });
    return changed;
  }, [formData, currentFyId]);
  const draft = useFormDraft({
    formId: 'ds-wms:proposal:new',
    enabled: isOpen && !isEdit,
    values: draftValues,
    onRestore: (v) => setFormData((prev) => ({ ...prev, ...(v as Partial<ProposalFormData>) })),
  });

  const resolvePriorityCode = useCallback((data: any) => {
    if (data?.prty_cd) return data.prty_cd;
    if (data?.prty_lvl_cd) return data.prty_lvl_cd;
    if (data?.prty_lvl_id) {
      const match = priorityLevels.find((priority) => priority.prty_lvl_id === data.prty_lvl_id);
      if (match?.prty_lvl_cd) return match.prty_lvl_cd;
    }
    return 'MEDIUM';
  }, [priorityLevels]);

  // Sub-schemes come from the legacy ERP sub_scheme table whose schemeid
  // refers to the legacy <schema>.scheme table — not wms_schme_lst_t.
  // We therefore show all active sub-schemes; client-side filtering by the
  // WMS schme_id would always return zero rows.

  // Load proposal data if editing
  const loadProposal = useCallback(async () => {
    setErrors({}); // Clear validation errors
    if (!proposalId) {
      setFormData(initialFormData);
      setWorksCount(0);
      setLinkedWorks([]);
      setWorkDrafts([{ wrk_nm: '', lctn_tx: '', allctd_at: 0 }]);
      return;
    }

    setLoading(true);
    try {
      const data = await proposalsApi.getById(proposalId);
      if (data) {
        // Parse geo_data if it's a string
        let geoData = data.geo_data;
        if (typeof geoData === 'string') {
          try {
            geoData = JSON.parse(geoData);
          } catch {
            geoData = null;
          }
        }

        setFormData({
          prpsl_cd: data.prpsl_cd || '',
          prpsl_nm: data.prpsl_nm || '',
          prpsl_dscn_tx: data.prpsl_dscn_tx || '',
          prpsl_srce_id: data.prpsl_srce_id || null,
          prpsl_type_cd: data.prpsl_type_cd || 'NEW',
          prcrmnt_type_cd: normaliseProcurementType(data.prcrmnt_type_cd),
          prpsl_sts_cd: data.prpsl_sts_cd || 'DRAFT',
          wrk_type_id: data.wrk_type_id || null,
          wrk_ctgry_id: data.wrk_ctgry_id || null,
          schme_id: data.schme_id || null,
          // Budgetary Details. These round-trip only since the columns were added
          // (server migration 136); before that the server had nowhere to store them,
          // so a saved proposal always reopened with the section blank.
          sub_schme_id: data.sub_schme_id || null,
          fndg_agncy_id: data.fndg_agncy_id || null,
          dpmt_id: data.dpmt_id || null,
          fnctn_id: data.fnctn_id || null,
          bdgt_hd_id: data.bdgt_hd_id || null,
          fnncl_yr_id: data.fnncl_yr_id || data.fy_id || null,
          uda_id: data.uda_id != null ? Number(data.uda_id) : null,
          zone_id: data.zone_id || null,
          ward_id: data.ward_id || null,
          lctn_tx: data.lctn_tx || '',
          estmt_cst_at: data.estmt_cst_at || 0,
          prpsd_by_nm: data.prpsd_by_nm || '',
          prpsd_by_dsgntn: data.prpsd_by_dsgntn || '',
          prpsd_by_cntct: data.prpsd_by_cntct || '',
          prpsl_dt: data.prpsl_dt ? data.prpsl_dt.split('T')[0] : todayYmd(),
          aprv_for_estmt_dt: data.aprv_for_estmt_dt ? data.aprv_for_estmt_dt.split('T')[0] : '',
          estmt_prprd_dt: data.estmt_prprd_dt ? data.estmt_prprd_dt.split('T')[0] : '',
          prty_cd: resolvePriorityCode(data),
          jstfctn_tx: data.jstfctn_tx || '',
          rmrks_tx: data.rmrks_tx || '',
          bnfcry_cnt: data.bnfcry_cnt || 0,
          bnfcry_dscn_tx: data.bnfcry_dscn_tx || '',
          geo_data: geoData || null
        });
        setWorksCount(data.works_count || 0);
        setLinkedWorks(Array.isArray(data.linked_works) ? data.linked_works : []);
        // Planned works saved with the proposal (migration 019), else one blank row.
        // Once works exist the planned list has been used up; offering it again would
        // re-create the same works, so a converted proposal starts with a blank row.
        const planned = Array.isArray(data.plnd_wrks_jn) && !(data.works_count > 0) ? data.plnd_wrks_jn : [];
        setWorkDrafts(planned.length
          ? planned.map((w: any) => ({ wrk_nm: w.wrk_nm || '', lctn_tx: w.lctn_tx || '', allctd_at: Number(w.allctd_at) || 0 }))
          : [{ wrk_nm: '', lctn_tx: data.lctn_tx || '', allctd_at: 0 }]);
      }
    } catch (error) {
      console.error('Error loading proposal:', error);
      showError('Failed to load proposal data');
    } finally {
      setLoading(false);
    }
  }, [proposalId, resolvePriorityCode, showError]);

  useEffect(() => {
    if (isOpen) {
      loadProposal();
    }
  }, [isOpen, loadProposal]);

  // Default a NEW proposal to the financial year flagged current (crnt_in).
  // Left unset, the proposal saves with fy_id NULL — the row then belongs to no
  // financial year even though its generated code names one. Only applied when
  // creating, and only while the field is still untouched, so an explicit choice
  // (or the value loaded when editing) is never overwritten.
  useEffect(() => {
    if (!isOpen || isEdit) return;
    if (formData.fnncl_yr_id) return;
    const current = financialYears.find((fy) => fy.crnt_in);
    if (current) {
      setFormData((prev) => (prev.fnncl_yr_id ? prev : { ...prev, fnncl_yr_id: current.fy_id }));
    }
  }, [isOpen, isEdit, financialYears, formData.fnncl_yr_id]);

  const handleInputChange = (field: keyof ProposalFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error when field changes
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Clear ward when zone changes
    if (field === 'zone_id') {
      setFormData(prev => ({ ...prev, zone_id: value as number | null, ward_id: null }));
    }
  };

  // Live completeness, recomputed on every keystroke. Drives the red alert banner and
  // the disabled state of the save button; validateForm() below reuses the same call so
  // the banner and the per-field errors can never disagree about what is missing.
  //
  // The four budgetary lists are passed as counts rather than arrays so the memo is not
  // invalidated by an identity change on an otherwise unchanged list.
  const { missing: missingRequired, unavailableMasters } = useMemo(
    () => collectMissingRequired(formData, {
      fndg_agncy_id: fundingAgencies?.length ?? 0,
      dpmt_id: departments?.length ?? 0,
      fnctn_id: functionCenters?.length ?? 0,
      bdgt_hd_id: budgetHeads?.length ?? 0,
    }),
    [formData, fundingAgencies, departments, functionCenters, budgetHeads]
  );

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    for (const { field, message } of missingRequired) {
      // First message per field wins — collectMissingRequired never emits a field twice,
      // but this keeps the mapping total rather than relying on that.
      if (!newErrors[field]) newErrors[field] = message;
    }

    const isValid = missingRequired.length === 0;
    setErrors(newErrors);

    if (!isValid) {
      const errorMessages = missingRequired.map((m) => m.label);
      if (errorMessages.length === 1) {
        showError(`Please fill the required field: ${errorMessages[0]}`);
      } else {
        showError(`Please fill the required fields: ${errorMessages.join(', ')}`);
      }
    } else if (unavailableMasters.length > 0) {
      // Saving is allowed, but say plainly which required values are going in empty
      // and why — otherwise this looks like the fields simply were not required.
      showWarning(
        `Saved without ${unavailableMasters.join(', ')} — no options are available. ` +
        'This usually means your profile lacks finance master access.'
      );
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    // Planned works are saved with the proposal while it is a new / Draft / Rejected
    // proposal. Same amount rule the server applies.
    if (canPlanWorks && namedDrafts.length > 0) {
      if (draftsMissingAmount > 0) { showError('Enter an allocated amount greater than 0 for every planned work'); return; }
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        fy_id: formData.fnncl_yr_id,
        prty_lvl_id: priorityLevels.find((priority) => priority.prty_lvl_cd === formData.prty_cd)?.prty_lvl_id || null,
        estmt_cst_at: parseFloat(String(formData.estmt_cst_at)) || 0,
        bnfcry_cnt: parseInt(String(formData.bnfcry_cnt)) || 0,
        // Sent only while the planned works can still change; the server refuses
        // them in any other status.
        ...(canPlanWorks ? {
          plnd_wrks_jn: namedDrafts.map((row) => ({
            wrk_nm: row.wrk_nm.trim(), lctn_tx: row.lctn_tx.trim(), allctd_at: Number(row.allctd_at) || 0,
          })),
        } : {})
      };

      if (isEdit && proposalId) {
        // Status is workflow-owned: proposals.service.js update() rejects any payload
        // carrying prpsl_sts_cd with a 409, precisely so a status cannot be advanced by
        // a plain field edit. This form spreads its whole state, so it always sent the
        // status back and every edit save was refused. Status changes go through the
        // submit / review / approve / reject actions instead.
        // The UDA is fixed once the proposal exists (the field is locked on edit), so it
        // is not sent back either.
        const { prpsl_sts_cd: _statusIsWorkflowOwned, uda_id: _udaFixed, ...updatePayload } = payload;
        await proposalsApi.update(proposalId, updatePayload);
        showSuccess('Proposal updated successfully');
      } else {
        await proposalsApi.create(payload);
        // Committed, so the draft is spent. Closing or cancelling keeps it.
        draft.clear();
        showSuccess('Proposal created successfully');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving proposal:', error);
      showError(error.response?.data?.message || 'Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };

  // Same gate the server enforces (PROPOSAL_CONVERTIBLE in proposals.service.js): only an
  // approved proposal can become a work. Offering the button earlier only produced a 409.
  const isConvertible = formData.prpsl_sts_cd === 'APPROVED' || formData.prpsl_sts_cd === 'AS_APPROVED';
  // Planned works (migration 019): entered and saved while the proposal is new, a Draft
  // or Rejected; shown read-only while it is under review; turned into works after
  // approval by "Create N works".
  const proposalSts = (formData.prpsl_sts_cd || 'DRAFT').toUpperCase();
  const canPlanWorks = !isEdit || proposalSts === 'DRAFT' || proposalSts === 'REJECTED';
  const plannedReadOnly = isEdit && (proposalSts === 'SUBMITTED' || proposalSts === 'UNDER_REVIEW');
  // One batch only: once converted, no more works can be added (also enforced by the server).
  const canAddMoreWorks = false;
  const canConvertToWork = isEdit && !!proposalId && isConvertible && worksCount === 0;

  const handleConvertToWork = async () => {
    if (!proposalId) return;

    setConverting(true);
    try {
      const result = await proposalsApi.createWork(proposalId);
      setFormData(prev => ({ ...prev, prpsl_sts_cd: 'CONVERTED' }));
      setWorksCount(1);
      showSuccess(`Work ${result?.workCode || ''} created from proposal. You can now create an estimate for it.`);
      onSuccess();
    } catch (error: any) {
      console.error('Error converting proposal to work:', error);
      showError(error.response?.data?.message || 'Failed to convert proposal to work');
    } finally {
      setConverting(false);
    }
  };

  const updateWorkDraft = (index: number, field: keyof ProposalWorkDraft, value: string | number) => {
    setWorkDrafts((rows) => rows.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  // Amount rule for splitting a proposal into works: every named work needs an amount,
  // and their total may not exceed the proposal's estimated cost. The server checks the
  // same, so this only spares the round trip.
  const proposalCost = Number(formData.estmt_cst_at) || 0;
  const namedDrafts = workDrafts.filter((row) => row.wrk_nm.trim());
  const draftsTotal = namedDrafts.reduce((sum, row) => sum + (Number(row.allctd_at) || 0), 0);
  const draftsMissingAmount = namedDrafts.filter((row) => !(Number(row.allctd_at) > 0)).length;
  // Already allocated to works created earlier (a converted proposal adding more works).
  const allocatedBefore = worksCount > 0
    ? linkedWorks.reduce((sum, w) => sum + (Number(w.allctd_at) || 0), 0)
    : 0;
  const draftsOverCost = proposalCost > 0 && allocatedBefore + draftsTotal > proposalCost;
  // Going over the proposal amount is shown as a warning but does not block creating
  // works (user decision 2026-10-03); only a missing name/amount does.
  const draftsBlocked = namedDrafts.length === 0 || draftsMissingAmount > 0;
  const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const handleCreateWorks = async () => {
    if (!proposalId) return;
    if (draftsMissingAmount > 0) {
      showError('Enter an allocated amount greater than 0 for every work');
      return;
    }
    const rows = workDrafts
      .filter((row) => row.wrk_nm.trim())
      .map((row) => ({
        wrk_nm: row.wrk_nm.trim(),
        lctn_tx: row.lctn_tx.trim() || formData.lctn_tx,
        allctd_at: Number(row.allctd_at) || 0,
      }));
    if (!rows.length) {
      showError('Add at least one work before converting the proposal');
      return;
    }
    setConverting(true);
    try {
      const result = await proposalsApi.createWorks(proposalId, rows);
      showSuccess(`${result?.works?.length || rows.length} work(s) created under this proposal`);
      // Reload so the "Works created from this proposal" list and totals are current.
      await loadProposal();
      onSuccess();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to create works from proposal');
    } finally {
      setConverting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[9999]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

      {/* Modal */}
      <div className={`flex min-h-full items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
        <div className={`relative bg-white shadow-xl transform transition-all ${
          isFullscreen
            ? 'w-full h-screen rounded-none'
            : 'w-full max-w-4xl rounded-xl'
        }`}>
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} ${
            isFullscreen ? '' : 'rounded-t-xl'
          }`}>
            <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className={WMS_POPUP_ICON}>
                  <i className="ri-lightbulb-line"></i>
                </div>
                <div className="min-w-0">
                  <h2 className={WMS_POPUP_TITLE}>
                    {isEdit ? 'Edit Proposal' : 'New Work Proposal'}
                  </h2>
                  <p className={WMS_POPUP_SUBTITLE}>
                    {isEdit ? `Editing: ${formData.prpsl_nm || formData.prpsl_cd}` : 'Create a new work proposal'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className={WMS_POPUP_CLOSE}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
                </button>
                <button
                  onClick={onClose}
                  className={WMS_POPUP_CLOSE}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`p-6 overflow-y-auto bg-gray-100 ${
            isFullscreen ? 'h-[calc(100vh-130px)]' : 'max-h-[70vh]'
          }`}>
            {loading ? (
              <ListSkeleton />
            ) : (
              <div className="space-y-6">
                <DraftRestoredBanner
                  show={!isEdit && draft.restored}
                  savedAt={draft.savedAt}
                  onClear={() => { draft.discard(); setFormData(initialFormData); setErrors({}); }}
                />

                {/* Works already created from the proposal. */}
                {isEdit && worksCount > 0 && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-slate-800">Works created from this proposal ({worksCount})</h3>
                    <div className="mt-2 divide-y divide-slate-200 bg-white rounded-lg border border-slate-200">
                      {linkedWorks.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-gray-500">{worksCount} work(s) linked.</p>
                      ) : linkedWorks.map((w) => (
                        <div key={w.wrk_id} className="px-3 py-2 flex items-center gap-3 text-sm">
                          <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{w.wrk_cd}</span>
                          <span className="flex-1 min-w-0 truncate text-gray-800">{w.wrk_nm}</span>
                          {w.wrk_sts_nm && <span className="text-xs text-gray-500">{w.wrk_sts_nm}</span>}
                          <span className="text-xs font-medium text-gray-700">₹{Number(w.allctd_at || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Works the proposal will be split into. Not offered on a new / Draft /
                    Rejected proposal any more; only a draft that already carries planned
                    works still shows them so they stay editable. Read-only under review,
                    and created as real works after approval by "Create N works". */}
                {((worksCount === 0 && ((canPlanWorks && namedDrafts.length > 0) || isConvertible || (plannedReadOnly && namedDrafts.length > 0))) || canAddMoreWorks) && (
                  <div className="bg-emerald-50/60 p-4 rounded-lg border border-emerald-200 shadow-sm">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="text-sm font-semibold text-emerald-900">
                          {canAddMoreWorks ? 'Add more works' : canPlanWorks ? 'Planned works (optional)' : 'Works in this proposal'}
                        </h3>
                        <p className="text-xs text-emerald-700 mt-1">
                          {canAddMoreWorks
                            ? 'Add works to this proposal, then click Create works. The total is compared with the proposal amount for information only.'
                            : canPlanWorks
                            ? 'List the works this proposal will be split into. They are saved with the proposal and created as works after approval. Leave empty for a single work.'
                            : plannedReadOnly
                              ? 'Planned works. They are created as works after the proposal is approved.'
                              : 'Check the works, then click Create works below. They are created under this proposal and grouped for one tender/contract.'}
                        </p>
                      </div>
                      {!plannedReadOnly && (
                        <button type="button" onClick={() => setWorkDrafts((rows) => [...rows, { wrk_nm: '', lctn_tx: '', allctd_at: 0 }])}
                          className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700">
                          <i className="ri-add-line mr-1"></i>Add work
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {workDrafts.map((row, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_150px_32px] gap-2 items-center">
                          <input value={row.wrk_nm} onChange={(e) => updateWorkDraft(index, 'wrk_nm', e.target.value)} disabled={plannedReadOnly}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" placeholder={`Work ${index + 1} name`} />
                          <input value={row.lctn_tx} onChange={(e) => updateWorkDraft(index, 'lctn_tx', e.target.value)} disabled={plannedReadOnly}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" placeholder="Work location" />
                          <input type="number" min="0" value={row.allctd_at || ''} onChange={(e) => updateWorkDraft(index, 'allctd_at', Number(e.target.value) || 0)} disabled={plannedReadOnly}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" placeholder="Allocated amount" />
                          <button type="button" disabled={plannedReadOnly || workDrafts.length === 1} onClick={() => setWorkDrafts((rows) => rows.filter((_, i) => i !== index))}
                            className="text-red-500 disabled:text-gray-300" title="Remove work"><i className="ri-delete-bin-line"></i></button>
                        </div>
                      ))}
                    </div>
                    {/* Running total against the proposal's estimated cost. */}
                    <div className={`mt-3 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-lg border ${
                      draftsMissingAmount > 0
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : draftsOverCost
                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                          : 'bg-white border-emerald-200 text-emerald-800'
                    }`}>
                      {/* The proposal amount is shown on its own and never changes here; the
                          works are listed beside it so nothing reads as added to it. */}
                      <span className="font-medium">
                        Proposal {proposalCost > 0 ? inr(proposalCost) : '(no estimated cost)'}
                      </span>
                      {allocatedBefore > 0 && (
                        <span className="text-gray-600">Existing works {inr(allocatedBefore)}</span>
                      )}
                      <span className="text-gray-600">{allocatedBefore > 0 ? 'New works' : 'Works'} {inr(draftsTotal)}</span>
                      {proposalCost > 0 && !draftsOverCost && (
                        <span className="text-gray-500">Remaining {inr(proposalCost - allocatedBefore - draftsTotal)}</span>
                      )}
                      {draftsOverCost && (
                        <span><i className="ri-error-warning-line mr-1"></i>Over the proposal by {inr(allocatedBefore + draftsTotal - proposalCost)}</span>
                      )}
                      {draftsMissingAmount > 0 && (
                        <span><i className="ri-error-warning-line mr-1"></i>{draftsMissingAmount} work(s) without an amount</span>
                      )}
                    </div>
                    {canAddMoreWorks && (
                      <div className="mt-3 flex justify-end">
                        <button type="button" onClick={handleCreateWorks} disabled={saving || converting || draftsBlocked}
                          title={draftsMissingAmount > 0 ? 'Every work needs an allocated amount' : undefined}
                          className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                          <i className={converting ? 'ri-loader-4-line animate-spin' : 'ri-building-2-line'}></i>
                          {converting ? 'Creating works...' : `Create ${namedDrafts.length || ''} work(s)`}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* What is being procured (server migration 217). Locked once the proposal
                    has become a work: the work carries its own copy from then on. */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    What are you procuring? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    {PROCUREMENT_TYPE_OPTIONS.map((opt) => {
                      const selected = formData.prcrmnt_type_cd === opt.code;
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          disabled={worksCount > 0}
                          onClick={() => handleInputChange('prcrmnt_type_cd', opt.code)}
                          className={`p-3 rounded-lg border-2 transition-all flex items-start gap-3 text-left disabled:opacity-60 disabled:cursor-not-allowed ${
                            selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <i className={`${opt.icon} text-2xl ${selected ? 'text-blue-600' : 'text-gray-500'}`}></i>
                          <span>
                            <span className={`block text-sm font-medium ${selected ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</span>
                            <span className="block text-xs text-gray-500">{opt.hint}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {formData.prcrmnt_type_cd === 'GOODS' && (
                    <p className="text-xs text-blue-700 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      Items marked with an asset category in the estimate are added to the asset register when their bill is paid.
                    </p>
                  )}
                </div>

                {/* Proposal Type Selection */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Proposal Type <span className="text-red-500">*</span>
                  </label>
                  {/* tabIndex -1: not an input, but the alert banner focuses whatever it
                      scrolls to, and a bare div is not focusable. */}
                  <div
                    id={requiredFieldAnchor('prpsl_type_cd')}
                    tabIndex={-1}
                    className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white p-4 rounded-lg border border-gray-200 shadow-inner outline-none"
                  >
                    {proposalTypes.map((type) => (
                      <button
                        key={type.prpsl_type_cd}
                        type="button"
                        onClick={() => handleInputChange('prpsl_type_cd', type.prpsl_type_cd)}
                        className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          formData.prpsl_type_cd === type.prpsl_type_cd
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className={`${type.icon_cd || 'ri-file-list-3-line'} text-2xl ${formData.prpsl_type_cd === type.prpsl_type_cd ? 'text-blue-600' : 'text-gray-500'}`}></i>
                        <span className={`text-sm font-medium ${formData.prpsl_type_cd === type.prpsl_type_cd ? 'text-blue-700' : 'text-gray-600'}`}>
                          {type.prpsl_type_nm}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Basic Information */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-file-list-3-line" title="Basic Information" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposal Code
                      </label>
                      <input
                        type="text"
                        value={formData.prpsl_cd}
                        onChange={(e) => handleInputChange('prpsl_cd', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Auto-generated"
                        disabled={isEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposal Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={requiredFieldAnchor('prpsl_dt')}
                        type="date"
                        value={formData.prpsl_dt}
                        onChange={(e) => handleInputChange('prpsl_dt', e.target.value)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.prpsl_dt ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                      />
                      {errors.prpsl_dt && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.prpsl_dt}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Financial Year <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('fnncl_yr_id')}
                        value={formData.fnncl_yr_id || ''}
                        onChange={(e) => handleInputChange('fnncl_yr_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.fnncl_yr_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Financial Year</option>
                        {financialYears.map((fy) => (
                          <option key={fy.fy_id} value={fy.fy_id}>
                            {fy.fy_nm}
                          </option>
                        ))}
                      </select>
                      {errors.fnncl_yr_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.fnncl_yr_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Approved for Estimate Date
                      </label>
                      <input
                        type="date"
                        value={formData.aprv_for_estmt_dt}
                        onChange={(e) => handleInputChange('aprv_for_estmt_dt', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estimate Prepared Date
                      </label>
                      <input
                        type="date"
                        value={formData.estmt_prprd_dt}
                        onChange={(e) => handleInputChange('estmt_prprd_dt', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposal Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={requiredFieldAnchor('prpsl_nm')}
                        type="text"
                        value={formData.prpsl_nm}
                        onChange={(e) => handleInputChange('prpsl_nm', e.target.value)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.prpsl_nm ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                        placeholder="Enter proposal title"
                      />
                      {errors.prpsl_nm && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.prpsl_nm}</p>
                      )}
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposal Description
                      </label>
                      <textarea
                        value={formData.prpsl_dscn_tx}
                        onChange={(e) => handleInputChange('prpsl_dscn_tx', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Detailed description of the proposal..."
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposal Source
                      </label>
                      <select
                        value={formData.prpsl_srce_id || ''}
                        onChange={(e) => handleInputChange('prpsl_srce_id', parseInt(e.target.value) || null)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                      >
                        <option value="">Select Proposal Source</option>
                        {proposalSources.map((source) => (
                          <option key={source.prpsl_srce_id} value={source.prpsl_srce_id}>
                            {source.prpsl_srce_nm}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Work Classification */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-folder-chart-line" title="Work Classification" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Work Category {formData.prcrmnt_type_cd === 'WORKS' && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        id={requiredFieldAnchor('wrk_ctgry_id')}
                        value={formData.wrk_ctgry_id || ''}
                        onChange={(e) => handleInputChange('wrk_ctgry_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.wrk_ctgry_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Category</option>
                        {workCategories.map((cat) => (
                          <option key={cat.wrk_ctgry_id} value={cat.wrk_ctgry_id}>
                            {cat.wrk_ctgry_nm}
                          </option>
                        ))}
                      </select>
                      {errors.wrk_ctgry_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.wrk_ctgry_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Work Type/Sector {formData.prcrmnt_type_cd === 'WORKS' && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        id={requiredFieldAnchor('wrk_type_id')}
                        value={formData.wrk_type_id || ''}
                        onChange={(e) => handleInputChange('wrk_type_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.wrk_type_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Work Type/Sector</option>
                        {workTypes.map((type) => (
                          <option key={type.wrk_type_id} value={type.wrk_type_id}>
                            {type.wrk_type_nm}
                          </option>
                        ))}
                      </select>
                      {errors.wrk_type_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.wrk_type_id}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Budgetary Details */}
                <div className="bg-green-50/50 p-4 rounded-lg border border-green-200 shadow-sm">
                  <SectionHeader icon="ri-money-dollar-circle-line" title="Budgetary Details" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Funding Agency <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('fndg_agncy_id')}
                        value={formData.fndg_agncy_id || ''}
                        onChange={(e) => handleInputChange('fndg_agncy_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.fndg_agncy_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-green-50/30'
                        }`}
                      >
                        <option value="">Select Funding Agency</option>
                        {fundingAgencies.map((agency) => (
                          <option key={agency.fndg_agncy_id} value={agency.fndg_agncy_id}>
                            {agency.fndg_agncy_nm}
                          </option>
                        ))}
                      </select>
                      {errors.fndg_agncy_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.fndg_agncy_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('dpmt_id')}
                        value={formData.dpmt_id || ''}
                        onChange={(e) => handleInputChange('dpmt_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.dpmt_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-green-50/30'
                        }`}
                      >
                        <option value="">Select Department</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                      {errors.dpmt_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.dpmt_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Function / Cost Center <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('fnctn_id')}
                        value={formData.fnctn_id || ''}
                        onChange={(e) => handleInputChange('fnctn_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.fnctn_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-green-50/30'
                        }`}
                      >
                        <option value="">Select Function</option>
                        {functionCenters.map((func) => (
                          <option key={func.fnctn_id} value={func.fnctn_id}>
                            {func.fnctn_nm}
                          </option>
                        ))}
                      </select>
                      {errors.fnctn_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.fnctn_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Budget Head <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('bdgt_hd_id')}
                        value={formData.bdgt_hd_id || ''}
                        onChange={(e) => handleInputChange('bdgt_hd_id', parseInt(e.target.value) || null)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.bdgt_hd_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-green-50/30'
                        }`}
                      >
                        <option value="">Select Budget Head</option>
                        {budgetHeads.map((head) => (
                          <option key={head.bdgt_hd_id} value={head.bdgt_hd_id}>
                            {head.bdgt_hd_nm}
                          </option>
                        ))}
                      </select>
                      {errors.bdgt_hd_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.bdgt_hd_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scheme <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={requiredFieldAnchor('schme_id')}
                        value={formData.schme_id || ''}
                        onChange={(e) => {
                          handleInputChange('schme_id', parseInt(e.target.value) || null);
                          handleInputChange('sub_schme_id', null); // Reset sub-scheme when scheme changes
                        }}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-green-50/30 ${
                          errors.schme_id ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Scheme</option>
                        {schemes.map((scheme) => (
                          <option key={scheme.schme_id} value={scheme.schme_id}>
                            {scheme.schme_nm}
                          </option>
                        ))}
                      </select>
                      {errors.schme_id && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.schme_id}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sub Scheme
                      </label>
                      <select
                        value={formData.sub_schme_id || ''}
                        onChange={(e) => handleInputChange('sub_schme_id', parseInt(e.target.value) || null)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-green-50/30"
                      >
                        <option value="">Select Sub Scheme</option>
                        {subSchemes.map((sub) => (
                          <option key={sub.sub_schme_id} value={sub.sub_schme_id}>
                            {sub.sub_schme_nm}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-map-pin-line" title="Location Information" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    {/* The UDA this proposal belongs to. Mandatory, and fixed once saved. */}
                    <div id={requiredFieldAnchor('uda_id')} tabIndex={-1} className="md:col-span-3">
                      <UdaRequiredField
                        value={formData.uda_id}
                        onChange={(v) => handleInputChange('uda_id', v)}
                        locked={isEdit}
                        error={errors.uda_id}
                        hint={isEdit ? 'The UDA cannot be changed after the proposal is created.' : 'Works created from this proposal will belong to this UDA.'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beneficiary Count
                      </label>
                      <input
                        type="number"
                        value={formData.bnfcry_cnt || ''}
                        onChange={(e) => handleInputChange('bnfcry_cnt', parseInt(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Estimated beneficiaries"
                        min="0"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beneficiary Description
                      </label>
                      <textarea
                        value={formData.bnfcry_dscn_tx}
                        onChange={(e) => handleInputChange('bnfcry_dscn_tx', e.target.value)}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Who benefits from this work"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location Details
                      </label>
                      <textarea
                        value={formData.lctn_tx}
                        onChange={(e) => handleInputChange('lctn_tx', e.target.value)}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Detailed location description (landmark, street, area)"
                      />
                    </div>
                  </div>
                </div>

                {/* Geo Location */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-map-2-line" title="Geo Location" />
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <p className="text-sm text-gray-500 mb-3">
                      Use the drawing tools on the left side of the map to mark locations, draw routes, or outline areas.
                    </p>
                    <GeoLocation
                      value={formData.geo_data}
                      onChange={(geoData) => setFormData(prev => ({ ...prev, geo_data: geoData }))}
                      center={mapView.center}
                      zoom={mapView.zoom}
                      height="350px"
                    />
                  </div>
                </div>

                {/* Financial & Priority */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-money-rupee-circle-line" title="Financial Information & Priority" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estimated Cost <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">₹</span>
                        <input
                          id={requiredFieldAnchor('estmt_cst_at')}
                          type="number"
                          value={formData.estmt_cst_at || ''}
                          onChange={(e) => handleInputChange('estmt_cst_at', parseFloat(e.target.value) || 0)}
                          className={`w-full border rounded-lg pl-8 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                            errors.estmt_cst_at ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                          min="0"
                        />
                      </div>
                      {errors.estmt_cst_at ? (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.estmt_cst_at}</p>
                      ) : formData.estmt_cst_at > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          ₹{formData.estmt_cst_at.toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposed By <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={requiredFieldAnchor('prpsd_by_nm')}
                        type="text"
                        value={formData.prpsd_by_nm}
                        onChange={(e) => handleInputChange('prpsd_by_nm', e.target.value)}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.prpsd_by_nm ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                        placeholder="Name of proposer"
                      />
                      {errors.prpsd_by_nm && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.prpsd_by_nm}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposer Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={requiredFieldAnchor('prpsd_by_dsgntn')}
                        type="text"
                        value={formData.prpsd_by_dsgntn}
                        onChange={(e) => handleInputChange('prpsd_by_dsgntn', e.target.value)}
                        maxLength={100}
                        className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30 ${
                          errors.prpsd_by_dsgntn ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                        }`}
                        placeholder="Designation of proposer"
                      />
                      {errors.prpsd_by_dsgntn && (
                        <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.prpsd_by_dsgntn}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposer Contact
                      </label>
                      <input
                        type="text"
                        value={formData.prpsd_by_cntct}
                        onChange={(e) => handleInputChange('prpsd_by_cntct', e.target.value)}
                        maxLength={50}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Phone or email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      {/* flex-wrap + a min width so the row reflows instead of
                          overflowing the column when more than three priority levels
                          are configured (flex-1 alone will not shrink a button below
                          its label width). */}
                      <div className="flex flex-wrap gap-2">
                        {priorityLevels.map((priority) => {
                          const selected = formData.prty_cd === priority.prty_lvl_cd;
                          // clr_cd holds a hex colour, as it does everywhere else in the
                          // module (PanelsDashboard, BiddersSection use it via style).
                          // It was previously interpolated into className, where a hex
                          // value is not a valid class and the selected state rendered
                          // unstyled.
                          const hex = priority.clr_cd || '#6B7280';
                          return (
                            <button
                              key={priority.prty_lvl_cd}
                              type="button"
                              onClick={() => handleInputChange('prty_cd', priority.prty_lvl_cd)}
                              className={`flex-1 min-w-[72px] py-2 px-3 rounded-lg border transition-all text-sm font-medium ${
                                selected ? '' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                              style={selected ? { backgroundColor: `${hex}1A`, color: hex, borderColor: hex } : undefined}
                            >
                              {priority.prty_lvl_nm.split(' ')[0]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Justification & Remarks */}
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200 shadow-sm">
                  <SectionHeader icon="ri-file-text-line" title="Justification & Remarks" />
                  <div className="grid grid-cols-1 gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Justification
                      </label>
                      <textarea
                        value={formData.jstfctn_tx}
                        onChange={(e) => handleInputChange('jstfctn_tx', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Explain the need and importance of this work..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remarks
                      </label>
                      <textarea
                        value={formData.rmrks_tx}
                        onChange={(e) => handleInputChange('rmrks_tx', e.target.value)}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50/30"
                        placeholder="Any additional remarks..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`border-t px-6 py-4 bg-gray-50 flex items-center justify-between ${
            isFullscreen ? '' : 'rounded-b-xl'
          }`}>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {canConvertToWork && (
                <button
                  onClick={handleConvertToWork}
                  disabled={saving || converting}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {converting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Converting...
                    </>
                  ) : (
                    <>
                      <i className="ri-building-line"></i>
                      Convert to Work
                    </>
                  )}
                </button>
              )}
              {isEdit && !!proposalId && worksCount === 0 && isConvertible && (
                <button onClick={handleCreateWorks} disabled={saving || converting || draftsBlocked}
                  title={draftsMissingAmount > 0 ? 'Every work needs an allocated amount' : undefined}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  <i className={converting ? 'ri-loader-4-line animate-spin' : 'ri-building-2-line'}></i>
                  {converting ? 'Creating works...' : `Create ${workDrafts.filter((row) => row.wrk_nm.trim()).length || ''} works`}
                </button>
              )}
              {/* Gated on completeness, so the operator cannot reach a rejected save at
                  all. The tooltip names what is still missing, because a disabled button
                  with no explanation is worse than one that fails loudly. */}
              <button
                onClick={handleSave}
                disabled={saving || converting || missingRequired.length > 0}
                title={
                  missingRequired.length > 0
                    ? `Fill the required fields first: ${missingRequired.map((m) => m.label).join(', ')}`
                    : undefined
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    {isEdit ? 'Update Proposal' : 'Create Proposal'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
